import { useMutation } from '@apollo/client/react';
import { Alert, Button, Form, Radio } from 'antd';
import React, { useState } from 'react';
import { toast } from '../../../../../apollo';
import CommonBulkConfirmationModal from '../../../../../components/CommonBulkConfirmationModal';
import CommonDrawer from '../../../../../components/CommonDrawer';
import { BULK_UPDATE_EQC_TYPE_STAGES } from '../../../graphql/Mutation';

const BulkEditStageListDrawer = ({
  showDrawer,
  setShowDrawer,
  selectedRows,
  isSelectAllAcrossPages,
  exceptIds,
  paginationProp,
  bulkAction,
  getBulkEqcStageJobStatus,
  startJobTracking,
  onSuccess,
  eqcTypeId,
}) => {
  const [form] = Form.useForm();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [bulkUpdateStages, { loading: bulkUpdateLoading }] = useMutation(
    BULK_UPDATE_EQC_TYPE_STAGES,
    {
      onCompleted(response) {
        const { status, jobId } = response?.bulkUpdateEqcTypeStages || {};

        if (status === 'SUCCESS' && jobId) {
          startJobTracking(jobId, bulkAction);
          getBulkEqcStageJobStatus({
            variables: { jobId },
          });
        }

        onSuccess?.();
        setShowDrawer(false);
      },
      onError() {},
    },
  );

  const onFormSubmit = async (values) => {
    if (Object.values(values).every((value) => value == null)) {
      toast({
        message: 'Please select at least one field to update',
        type: 'warning',
      });
      return;
    }
    try {
      const formattedValues = {};
      if (values?.witness !== undefined) {
        formattedValues.witness = values.witness === 'on';
      }
      if (values?.drawing !== undefined) {
        formattedValues.drawing = values.drawing === 'on';
      }
      const mutationData = { ...formattedValues };

      if (isSelectAllAcrossPages) {
        mutationData.isAllSelected = true;
        mutationData.eqcTypeId = Number(eqcTypeId);
        if (exceptIds?.length > 0) {
          mutationData.exceptEqcTypeStageIds = exceptIds;
        }
      } else {
        mutationData.eqcTypeStageIds =
          selectedRows?.map((row) => row?.id) || [];
      }

      await bulkUpdateStages({
        variables: {
          data: mutationData,
        },
      });
    } catch (error) {
      return error;
    }
  };

  const handleBulkEditCancel = () => {
    const hasFormChanges = form.isFieldsTouched();

    if (hasFormChanges) {
      setShowConfirmationModal(true);
    } else {
      form.resetFields();
      setShowDrawer(false);
    }
  };

  const handleConfirmDiscard = () => {
    form.resetFields();
    setShowDrawer(false);
  };

  return (
    <>
      <CommonDrawer
        title={`Bulk Edit (${
          isSelectAllAcrossPages
            ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
            : selectedRows?.length || 0
        } Stages Selected)`}
        open={showDrawer}
        onClose={handleBulkEditCancel}
        width={500}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('stage-list-container') || document.body
        }
      >
        <Alert
          className="mb-10"
          title="Note: Only the current changes will be updated. All other configurations will remain unchanged."
          type="info"
          showIcon
        />
        <Form form={form} layout="vertical" onFinish={onFormSubmit}>
          <Form.Item name="witness" label="Witness Required">
            <Radio.Group>
              <Radio value="on">On</Radio>
              <Radio value="off">Off</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="drawing" label="Drawing Required">
            <Radio.Group>
              <Radio value="on">On</Radio>
              <Radio value="off">Off</Radio>
            </Radio.Group>
          </Form.Item>
          <div className="form-buttons">
            <Button
              shape="round"
              className="cancel-button"
              onClick={handleBulkEditCancel}
              disabled={bulkUpdateLoading}
            >
              Cancel
            </Button>
            <Button
              shape="round"
              type="primary"
              className="save-button"
              loading={bulkUpdateLoading}
              htmlType="submit"
            >
              Save
            </Button>
          </div>
        </Form>
      </CommonDrawer>

      {showConfirmationModal && (
        <CommonBulkConfirmationModal
          showModal={showConfirmationModal}
          setShowModal={setShowConfirmationModal}
          onConfirm={handleConfirmDiscard}
          title="Unsaved Changes"
          message="You have changes that haven't been saved. Discard them?"
        />
      )}
    </>
  );
};

export default BulkEditStageListDrawer;
